import { memo, useEffect } from 'react';
import { Modal, Button, Space, Tooltip } from 'antd';
import {
  DownloadOutlined,
  LeftOutlined,
  RightOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import type { FreePhoto_Base } from '~/common/typescript/models/FreePhoto';
import { PhotoViewer } from '~/screens/FreePhotoScreen/components/PhotoViewer';
import { PhotoMetadataPanel } from '~/screens/FreePhotoScreen/components/PhotoMetadataPanel';
import { useFreePhotoMetadata } from '~/screens/FreePhotoScreen/hooks/useFreePhotoMetadata';
import { useFreePhotoDownload } from '~/screens/FreePhotoScreen/hooks/useFreePhotoDownload';

interface FreePhotoDetailModalProps {
  visible: boolean;
  photo: FreePhoto_Base | null;
  photos: FreePhoto_Base[];
  onClose: () => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
}

const FreePhotoDetailModal = ({
  visible,
  photo,
  photos,
  onClose,
  onNavigate,
}: FreePhotoDetailModalProps) => {
  const metadata = useFreePhotoMetadata(photo);
  const { downloadPhoto, downloading } = useFreePhotoDownload();

  // Keyboard navigation
  useEffect(() => {
    if (!visible || !onNavigate) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') onNavigate('prev');
      if (e.key === 'ArrowRight') onNavigate('next');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [visible, onNavigate]);

  if (!photo) return null;

  const currentIndex = photos.findIndex((p) => p.uuid === photo.uuid);
  const total = photos.length;
  const hasPrev = total > 1;
  const hasNext = total > 1;

  const lat = metadata?.location?.coords?.latitude;
  const lng = metadata?.location?.coords?.longitude;

  const handleZoomToLocation = () => {
    if (lat != null && lng != null) {
      const map = window.map;
      if (map) {
        map.flyTo({ center: [lng, lat], zoom: 17, essential: true, duration: 1000 });
        onClose();
      }
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      width="85vw"
      style={{ top: 20 }}
      footer={null}
      destroyOnClose
      title={
        <UI_ModalHeader>
          {/* Navigation */}
          <UI_NavGroup>
            {onNavigate && (
              <>
                <Tooltip title="Ảnh trước (←)">
                  <Button
                    icon={<LeftOutlined />}
                    onClick={() => onNavigate('prev')}
                    disabled={!hasPrev}
                    size="small"
                  />
                </Tooltip>
                {total > 0 && (
                  <UI_Counter>
                    {currentIndex + 1} / {total}
                  </UI_Counter>
                )}
                <Tooltip title="Ảnh tiếp (→)">
                  <Button
                    icon={<RightOutlined />}
                    onClick={() => onNavigate('next')}
                    disabled={!hasNext}
                    size="small"
                  />
                </Tooltip>
              </>
            )}
          </UI_NavGroup>

          {/* Actions */}
          <Space>
            {lat != null && (
              <Button icon={<EnvironmentOutlined />} size="small" onClick={handleZoomToLocation}>
                Trên bản đồ
              </Button>
            )}
            <Button
              icon={<DownloadOutlined />}
              size="small"
              loading={downloading}
              onClick={() => downloadPhoto(photo.uuid, photo.file_name)}
            >
              Tải xuống
            </Button>
          </Space>
        </UI_ModalHeader>
      }
    >
      <UI_Content>
        {/* Image viewer — reused from FreePhotoScreen */}
        <UI_ImageSection>
          <PhotoViewer uuid={photo.uuid} alt={photo.file_name} />
        </UI_ImageSection>

        {/* Metadata panel — reused from FreePhotoScreen */}
        <UI_MetaSection>
          <PhotoMetadataPanel
            metadata={metadata}
            fileName={photo.file_name}
            createdAt={photo.created_at}
            projectId={photo.project_id}
            userId={photo.user_id}
          />
        </UI_MetaSection>
      </UI_Content>
    </Modal>
  );
};

export default memo(FreePhotoDetailModal);

/* ─── Styled Components ─────────────────────────────────────────────────────── */

const UI_ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 32px;
`;

const UI_NavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const UI_Counter = styled.span`
  font-size: 13px;
  color: #666;
  min-width: 48px;
  text-align: center;
  font-weight: normal;
`;

const UI_Content = styled.div`
  display: flex;
  height: 65vh;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
  }
`;

const UI_ImageSection = styled.div`
  flex: 1;
  min-width: 0;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 45vh;
  }
`;

const UI_MetaSection = styled.div`
  width: 300px;
  flex-shrink: 0;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
  }
`;
